import { Modal } from '@/components/common/dialog';
import { ModalButton } from '@/components/common/modal-button';
import { KEY_CODE_SIHEUNG } from '@/constants/app';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores/configStore';
import { toAriaLabelText } from '@/utils/tts';
import { useTranslation } from 'react-i18next';

interface VoiceGuideModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onConfirmAndNotRedirectHome: () => void;
}

export function VoiceGuideModal({ isOpen, onClose, onConfirm, onConfirmAndNotRedirectHome }: VoiceGuideModalProps) {
    const { t } = useTranslation();
    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
    const { isHorizontal } = useOrientation();
    const isSiheungVertical = KEY_CODE_SIHEUNG.includes(storeCode) && !isHorizontal;
    const description1Key = isSiheungVertical ? 'usedTTS.description1Siheung' : 'usedTTS.description1';
    const description2Key = isSiheungVertical ? 'usedTTS.description2Siheung' : 'usedTTS.description2';
    const description1AriaLabel = toAriaLabelText(t(description1Key));
    const description2AriaLabel = toAriaLabelText(t(description2Key));

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="tts-modal max-w-[calc(620*var(--vw-unit))]" closeRegionOrder={5} closeTabIndex={5}>
            <div className="flex h-full flex-col">
                <header className="flex h-[calc(90*var(--vh-unit))] items-end justify-center px-[calc(60*var(--vw-unit))]">
                    <h1
                        data-modal-region
                        tabIndex={1}
                        data-interactive
                        aria-label={t('usedTTS.title')}
                        className="focus-border text-center font-bold text-[calc(30*var(--vh-unit))] leading-[calc(42*var(--vh-unit))] tracking-[calc(-0.63*var(--vh-unit))] text-[var(--text-black-01)]"
                        data-modal-region-order={1}
                    >
                        {t('usedTTS.title')}
                    </h1>
                </header>
                <main className="w-full">
                    <div
                        className="focus-border flex flex-col gap-[calc(24*var(--vh-unit))] px-[calc(80*var(--vw-unit))] pt-[calc(60*var(--vh-unit))] pb-[calc(40*var(--vh-unit))] text-[var(--text-black-02)] leading-[calc(24*var(--vh-unit))]"
                        data-interactive
                        data-modal-region
                        data-modal-region-order={2}
                        tabIndex={2}
                        aria-label={`${t('usedTTS.subTitle1')} ${description1AriaLabel} ${t('usedTTS.subTitle2')} ${description2AriaLabel}`}
                    >
                        <div className="flex flex-col gap-[calc(6*var(--vh-unit))]">
                            <div className="text-[calc(22*var(--vh-unit))] font-bold leading-[calc(24*var(--vh-unit))] tracking-[calc(-0.132*var(--vh-unit))]">
                                {t('usedTTS.subTitle1')}
                            </div>
                            <div
                                className="text-[calc(18*var(--vh-unit))] font-medium leading-[calc(24*var(--vh-unit))] tracking-[calc(-0.108*var(--vh-unit))]"
                                dangerouslySetInnerHTML={{ __html: t(description1Key) }}
                            ></div>
                        </div>
                        <div className="flex flex-col gap-[calc(6*var(--vh-unit))]">
                            <div className="text-[calc(22*var(--vh-unit))] font-bold leading-[calc(24*var(--vh-unit))] tracking-[calc(-0.132*var(--vh-unit))]">
                                {t('usedTTS.subTitle2')}
                            </div>
                            <div
                                className="text-[calc(18*var(--vh-unit))] font-medium leading-[calc(24*var(--vh-unit))] tracking-[calc(-0.108*var(--vh-unit))]"
                                dangerouslySetInnerHTML={{ __html: t(description2Key) }}
                            ></div>
                        </div>
                    </div>
                </main>
                <footer className="flex items-center gap-[calc(12*var(--vw-unit))] justify-center px-[calc(20*var(--vw-unit))] pt-[calc(20*var(--vh-unit))] pb-[calc(40*var(--vh-unit))]">
                    <ModalButton
                        variant="secondary"
                        role="button"
                        aria-label={t('usedTTS.buttons.cancel')}
                        onClick={onConfirm}
                        data-interactive
                        type="button"
                        data-modal-region
                        data-modal-region-order={3}
                        tabIndex={3}
                        className="h-[calc(96*var(--vh-unit))] w-[calc(200*var(--vw-unit))] rounded-[calc(12*var(--vh-unit))] text-[calc(26*var(--vh-unit))]"
                    >
                        {t('usedTTS.buttons.cancel')}
                    </ModalButton>
                    <ModalButton
                        variant="primary"
                        role="button"
                        aria-label={t('usedTTS.buttons.continue')}
                        onClick={onConfirmAndNotRedirectHome}
                        data-interactive
                        type="button"
                        data-modal-region
                        data-modal-region-order={4}
                        tabIndex={4}
                        className="h-[calc(96*var(--vh-unit))] w-[calc(200*var(--vw-unit))] rounded-[calc(12*var(--vh-unit))] text-[calc(26*var(--vh-unit))]"
                    >
                        {t('usedTTS.buttons.continue')}
                    </ModalButton>
                </footer>
            </div>
        </Modal>
    );
}
